<!-- eslint-disable @angular-eslint/template/cyclomatic-complexity -->
<!--
  SUB-MANAGER CYCLE FORMS COMPONENT

  View-only display of cycle forms assigned to a sub-manager with:
  - Breadcrumb navigation
  - Form list sidebar with completion stats
  - Activity tabs for multi-activity forms
  - Read-only question/answer display
  - Overall input tracking (root cause, mitigation)
  - Decision status display

  Note: This is a view-only component for system managers to monitor sub-manager progress.
-->

<div class="manager-cycle-form-container">
  <!-- ==================== BREADCRUMB NAVIGATION ==================== -->
  <nav class="breadcrumb-nav" aria-label="Breadcrumb">
    <a routerLink="/dashboard" class="breadcrumb-link">
      <mat-icon class="breadcrumb-icon">home</mat-icon>
      Dashboard
    </a>
    <mat-icon class="breadcrumb-separator">chevron_right</mat-icon>
    <a tabindex="0"
       role="button"
       class="breadcrumb-link breadcrumb-clickable"
       (click)="onBack()"
       (keyup.enter)="onBack()"
       (keyup.space)="onBack()">
      <mat-icon class="breadcrumb-icon">person</mat-icon>
      {{ managerName }}
    </a>
    @if (formData.cycleName) {
      <mat-icon class="breadcrumb-separator">chevron_right</mat-icon>
      <span class="breadcrumb-current">{{ formData.cycleName }}</span>
    }
  </nav>

  <!-- ==================== HEADER SECTION ==================== -->
  <div class="header-section">
    <div class="header-content">
      <div class="title-container">
        <h1 class="page-title">{{ formData.cycleName }}</h1>
        <mat-icon class="help-icon" tooltipPosition="right"
          [appBefairTooltip]="'View sub-manager cycle forms and track completion status.'">
          help_outline
        </mat-icon>
      </div>
      <p class="page-subtitle">View {{ managerName }}'s cycle forms and track completion status</p>
    </div>
  </div>

  <!-- ==================== MAIN CONTENT LAYOUT ==================== -->
  <div class="content-wrapper">
    <div class="main-content" [class.sidebar-collapsed]="isSidebarCollapsed">
      <!-- ========== SIDEBAR - Forms List ========== -->
      <div class="sidebar" [class.collapsed]="isSidebarCollapsed">
        <div class="sidebar-header">
          <button mat-icon-button
                  class="sidebar-toggle-btn"
                  [attr.aria-label]="isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'"
                  [appBefairTooltip]="isSidebarCollapsed ? 'Expand forms list' : 'Collapse forms list'"
                  (click)="toggleSidebar()">
            <mat-icon>{{ isSidebarCollapsed ? 'chevron_right' : 'chevron_left' }}</mat-icon>
          </button>
          @if (!isSidebarCollapsed) {
            <mat-icon class="sidebar-icon">assignment</mat-icon>
            <h3 class="sidebar-title">{{ totalForms }} {{ totalForms === 1 ? 'Form' : 'Forms' }}</h3>
          }
        </div>

        @if (!isSidebarCollapsed) {
        <div class="forms-list" role="list">
          @for (form of formData.forms; track $index) {
            <button class="form-item"
                    [class.selected]="form.isSelected"
                    [class.active]="form.isSelected"
                    [attr.aria-label]="'Select form: ' + form.name"
                    [attr.aria-current]="form.isSelected ? 'true' : 'false'"
                    (click)="selectForm(form)">
              <div class="form-indicator">
                <span class="form-number">{{ $index + 1 }}</span>
              </div>
              <div class="form-info">
                <div class="form-name">{{ form.name }}</div>
                <div class="form-meta">
                  <span>{{ form.activity_count || 0 }} activities</span>
                  <span class="completion-text">{{ form.completion_percentage || 0 }}%</span>
                  @if (formCompletedCache.get(form.id)) {
                    <span class="completed-badge" title="Completed"><mat-icon>check_circle</mat-icon></span>
                  }
                  @if (formNotApplicableCache.get(form.id)) {
                    <span class="no-answers-badge">Not Applicable</span>
                  }
                  @if (formNoAnswersCache.get(form.id)) {
                    <span class="no-answers-badge">No: {{ formNoAnswersCache.get(form.id) }}</span>
                  }
                </div>
              </div>
              <mat-icon class="chevron-icon">chevron_right</mat-icon>
            </button>
          }
        </div>
        }
      </div>

      <!-- ========== MAIN CONTENT AREA ========== -->
      <div class="content-panel">
        <!-- Manager Summary Card -->
        <section class="summary-section">
          <div class="manager-summary-card">
            <div class="card-header">
              <h3 class="card-title">
                <mat-icon class="card-icon">person</mat-icon>
                Manager Overview
              </h3>
              <span class="view-only-badge">
                <mat-icon>visibility</mat-icon>
                View Only
              </span>
            </div>
            <div class="summary-grid">
              <div class="summary-item">
                <mat-icon class="summary-icon">person</mat-icon>
                <div class="summary-content">
                  <span class="summary-label">Manager</span>
                  <span class="summary-value">{{ formData.managerName }}</span>
                </div>
              </div>

              <div class="summary-item">
                <mat-icon class="summary-icon">group</mat-icon>
                <div class="summary-content">
                  <span class="summary-label">Activities</span>
                  <span class="summary-value">{{ totalActivityOwners }}</span>
                </div>
              </div>

              <div class="summary-item">
                <mat-icon class="summary-icon">schedule</mat-icon>
                <div class="summary-content">
                  <span class="summary-label">Deadline</span>
                  <span class="summary-value">{{ managerDeadline }}</span>
                </div>
              </div>

              <div class="summary-item completion-item">
                <mat-icon class="summary-icon">analytics</mat-icon>
                <div class="summary-content">
                  <span class="summary-label">Completion Status</span>
                  <div class="completion-status-container">
                    <div class="progress-bar">
                      <div class="progress-fill" [ngClass]="completionStatusClass" [style.width.%]="overallCompletion"></div>
                    </div>
                    <span class="status-text">{{ overallCompletion }}% Complete</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- Form Content -->
        @if (selectedForm) {
          <section class="form-section">
            <div class="form-content">
              <!-- Form Header with Title and Activity Tabs -->
              <div class="form-header">
                <div class="form-title-container">
                  <h2 class="form-title">
                    <mat-icon class="form-icon">description</mat-icon>
                    {{ selectedForm.name }}
                    @if (selectedForm.is_form_not_applicable) {
                      <span class="not-applicable-badge" [appBefairTooltip]="selectedForm.form_not_applicable_reason || 'This form is not applicable'">
                        <mat-icon>block</mat-icon>
                        Not Applicable
                      </span>
                    }
                  </h2>
                </div>

                <!-- Activity Tabs -->
                @if (activityTabs.length > 0 && !selectedForm.is_form_not_applicable) {
                  <div class="activity-tabs">
                    @for (tab of activityTabs; track tab.activity_number; let i = $index) {
                      <app-activity-tab
                        [activityNumber]="tab.activity_number"
                        [completionCount]="tab.total_answers"
                        [totalQuestions]="totalQuestionsCount"
                        [noAnswerCount]="tab.no_answers"
                        [activityStatus]="tab.activity_status ?? undefined"
                        [displayName]="tab.display_name"
                        [isActive]="i === selectedActivityIndex"
                        (selectTab)="selectActivity(i)" />
                    }
                  </div>
                }
              </div>

              <!-- Form Not Applicable Message -->
              @if (selectedForm.is_form_not_applicable) {
                <div class="empty-state-card">
                  <mat-icon class="empty-icon">block</mat-icon>
                  <h3>This form has been marked as "Not Applicable" by the Manager</h3>
                  <p>{{ selectedForm.form_not_applicable_reason || 'This form has been marked as not applicable.' }}</p>
                </div>
              } @else {

              <!-- ========== EMPTY STATES ========== -->
              <!-- No Activities Message -->
              @if (!hasActivities && !selectedForm.is_form_not_applicable) {
                <div class="empty-state-card">
                  <mat-icon class="empty-icon">info_outline</mat-icon>
                  <h3>No Activities Available</h3>
                  <p>There are no activities recorded for this form yet. Questions with answers will be displayed once activities are added.</p>
                </div>
              }

              <!-- ========== FILTER CONTROLS & ACTIVITY DETAILS ========== -->
              @if (hasActivities && !selectedForm.is_form_not_applicable) {
                <div class="filter-section">
                  @if (activityTabs[selectedActivityIndex]) {
                    <div class="activity-info-inline">
                      <div class="activity-detail-item">
                        <mat-icon class="detail-icon">badge</mat-icon>
                        <span class="detail-label">Reference Number:</span>
                        <span class="detail-value">{{ activityTabs[selectedActivityIndex].reference_number || 'N/A' }}</span>
                      </div>
                      <div class="activity-detail-item">
                        <mat-icon class="detail-icon">person</mat-icon>
                        <span class="detail-label">Activity Owner:</span>
                        <span class="detail-value">{{ activityTabs[selectedActivityIndex].display_name || 'N/A' }}</span>
                      </div>
                    </div>
                  }

                  <mat-checkbox
                    class="filter-checkbox"
                    [(ngModel)]="formData.showOnlyAnswersWithNo"
                    (ngModelChange)="onFilterChange()">
                    <mat-icon class="filter-icon">filter_list</mat-icon>
                    Show only Answers with "No"
                  </mat-checkbox>
                </div>
              }

              <!-- ========== QUESTIONS BY STAGE ========== -->
              @if (hasActivities && !selectedForm.is_form_not_applicable) {
                <div class="stages-container">
                @for (stage of allStages; track stage.id) {
                  <div class="stage-group">
                    <div class="stage-header">
                      <mat-icon class="stage-icon">layers</mat-icon>
                      <h3 class="stage-title">{{ stage.name }}</h3>
                      <span class="stage-count">{{ stage.questions.length }} Questions</span>
                    </div>

                    <!-- Questions List -->
                    <div class="questions-container">
                      @for (question of stage.questions; track question.id; let qIdx = $index) {
                        @if (question.showQuestion) {
                          <div class="question-card">
                            <div class="question-header">
                              <div class="question-content">
                                <span class="question-text">{{ question.questionNumber }}. {{ question.text }}</span>
                              </div>
                            </div>

                            <div class="question-answer">
                              <div class="answer-section">
                                <!-- Display answer for yes_no and yes_no_na -->
                                @if (question.type === 'yes_no' || question.type === 'yes_no_na') {
                                  <div class="answer-display">
                                    <span class="answer-label">Answer:</span>
                                    <span class="answer-value" [class.answer-yes]="question.answer === 'yes'"
                                          [class.answer-no]="question.answer === 'no'"
                                          [class.answer-na]="question.answer === 'na'">
                                      {{ question.answerText || 'No answer provided' }}
                                    </span>
                                  </div>
                                }

                                <!-- Display answer for text type -->
                                @if (question.type === 'text') {
                                  <div class="answer-display">
                                    <span class="answer-label">Answer:</span>
                                    <span class="answer-value text-answer-value">
                                      {{ question.answer || 'No answer provided' }}
                                    </span>
                                  </div>
                                }

                                <!-- Display answer for number type -->
                                @if (question.type === 'number') {
                                  <div class="answer-display">
                                    <span class="answer-label">Answer:</span>
                                    <span class="answer-value">
                                      {{ question.answer !== undefined ? question.answer : 'No answer provided' }}
                                    </span>
                                  </div>
                                }
                              </div>

                              <!-- Comments Section -->
                              @if (question.comments) {
                                <div class="comments-display">
                                  <div class="comments-header">
                                    <mat-icon class="label-icon">comment</mat-icon>
                                    <span class="comments-label">Comments</span>
                                  </div>
                                  <div class="comments-text">
                                    {{ question.comments }}
                                  </div>
                                </div>
                              }

                              <!-- Attachment Section -->
                              @if (question.attachment) {
                                <div class="attachment-section">
                                  <div class="attachment-label">
                                    <mat-icon class="label-icon">attach_file</mat-icon>
                                    Attachment
                                  </div>
                                  <div class="attachment-item">
                                    <mat-icon class="attachment-icon">description</mat-icon>
                                    <span class="attachment-name">{{ question.attachment }}</span>
                                    <button mat-icon-button
                                            class="download-btn"
                                            tooltipPosition="above"
                                            [appBefairTooltip]="'Download file'"
                                            [attr.aria-label]="'Download ' + question.attachment"
                                            (click)="downloadAttachment(question)">
                                      <mat-icon>download</mat-icon>
                                    </button>
                                  </div>
                                </div>
                              }
                            </div>
                          </div>
                        }
                      }
                    </div>
                  </div>
                }
              </div>
              }

              <!-- ========== OVERALL INPUT - Read Only Display ========== -->
              @if (shouldShowOverallInput) {
                <div class="overall-input-display">
                  <div class="overall-input-header">
                    <mat-icon class="section-icon">assessment</mat-icon>
                    <h3>Overall Input</h3>
                  </div>

                  <div class="overall-input-grid">
                    <!-- Root Cause -->
                    <div class="input-display-group">
                      <div class="input-label">
                        <mat-icon class="label-icon">flag</mat-icon>
                        Root Cause
                      </div>
                      <div class="input-value">
                        {{ rootCauseDisplayText }}
                      </div>
                    </div>

                    <!-- Mitigation Action -->
                    <div class="input-display-group">
                      <div class="input-label">
                        <mat-icon class="label-icon">build</mat-icon>
                        Mitigation Action
                      </div>
                      <div class="input-value">
                        {{ mitigationActionDisplayText }}
                      </div>
                    </div>

                    <!-- Monitoring Status -->
                    <div class="input-display-group">
                      <div class="input-label">
                        <mat-icon class="label-icon">check_circle</mat-icon>
                        Monitoring Complete
                      </div>
                      <div class="input-value">
                        <span class="status-badge" [class.confirmed]="isMonitoringConfirmed">
                          <mat-icon>{{ monitoringStatusIcon }}</mat-icon>
                          {{ monitoringStatusText }}
                        </span>
                      </div>
                    </div>

                    <!-- Comments -->
                    @if (currentActivityOverallInput.comments) {
                      <div class="input-display-group full-width">
                        <div class="input-label">
                          <mat-icon class="label-icon">comment</mat-icon>
                          Comments
                        </div>
                        <div class="input-value comments-value">
                          {{ currentActivityOverallInput.comments }}
                        </div>
                      </div>
                    }
                  </div>
                </div>
              }

              <!-- ========== DECISION STATUS - Read Only Display ========== -->
              @if (shouldShowDecisionStatusSection) {
                <div class="overall-input-display decision-status-display">
                  <div class="overall-input-header">
                    <mat-icon class="section-icon">gavel</mat-icon>
                    <h3>Decision Status</h3>
                  </div>

                  <div class="overall-input-grid">
                    <!-- Decision Status Badge -->
                    <div class="input-display-group full-width">
                      <div class="input-label">
                        <mat-icon class="label-icon">gavel</mat-icon>
                        Status
                      </div>
                      <div class="input-value">
                        <div class="status-badge decision-badge"
                             [class.approved]="isApprovedStatus"
                             [class.rejected]="isRejectedStatus">
                          <mat-icon>{{ decisionStatusIcon }}</mat-icon>
                          {{ decisionStatusText }}
                        </div>
                      </div>
                    </div>

                    <!-- Approver Information -->
                    @if (currentActivityOverallInput.approvedBy) {
                      <div class="input-display-group full-width">
                        <div class="input-label">
                          <mat-icon class="label-icon">person</mat-icon>
                          {{ approverLabel }}
                        </div>
                        <div class="input-value">
                          {{ currentActivityOverallInput.approvedBy }}
                        </div>
                      </div>
                    }

                    <!-- Approver Comments -->
                    @if (currentActivityOverallInput.approverComments) {
                      <div class="input-display-group full-width">
                        <div class="input-label">
                          <mat-icon class="label-icon">comment</mat-icon>
                          Compliance BP Comments
                        </div>
                        <div class="input-value comments-value">
                          {{ currentActivityOverallInput.approverComments }}
                        </div>
                      </div>
                    }
                  </div>
                </div>
              }
              }
            </div>
          </section>
        }
      </div>
    </div>
  </div>
</div>
